import {
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ArrowUpward from '@mui/icons-material/ArrowUpward'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'

import useStyles from './ReorderableAccordionItem.styles'

export type AccordionItemType = {
    title: string,
    content?: React.JSX.Element,
    renderContent?: (currentHeight: number) => React.JSX.Element,
    key: string,
}

const InitialHeightPx = 400
const MinHeightPx = 200
const MaxHeightPx = 600

type ReorderableAccordionItemProps = {
    item: AccordionItemType,
    moveItem: (index: number, direction: 'up' | 'down') => void,
    index: number,
    first: boolean,
    last: boolean,
    initialHeightVh?: number,
}

export default function ReorderableAccordionItem({
    item,
    moveItem,
    index,
    first,
    last,
    initialHeightVh,
}: Readonly<ReorderableAccordionItemProps>) {
    const {
        classes,
        cx,
    } = useStyles()
    const {
        title,
        key,
        renderContent,
        content,
    } = item
    const containerRef = useRef(null)
    const [
        currentHeight,
        setCurrentHeight,
    ] = useState(InitialHeightPx)

    const contentChild = useMemo(() => {
        return renderContent ? renderContent(currentHeight) : content
    }, [
        content,
        renderContent,
        currentHeight,
    ])

    useLayoutEffect(() => {
        const observer = new ResizeObserver((entries) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const entry of entries) {
                const newHeightPx = entry.contentRect.height

                setCurrentHeight(newHeightPx)
            }
        })

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => { return observer.disconnect() }
    }, [])

    return (
        <Accordion
            key={key}
            defaultExpanded
            className={classes.accordion}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                className={classes.accordionSummary}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        width: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    <div className={classes.headerTitle}>{title}</div>
                    <Stack direction="row">
                        <IconButton
                            size="small"
                            className={cx(first && classes.disabledButton)}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (first) return
                                moveItem(index, 'up')
                            }}
                        >
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            className={cx(last && classes.disabledButton)}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (last) return
                                moveItem(index, 'down')
                            }}
                        >
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{
                p: 0,
            }}
            >
                <div
                    ref={containerRef}
                    style={{
                        height: `${initialHeightVh}vh`,
                        minHeight: `${MinHeightPx}px`,
                        maxHeight: `${MaxHeightPx}px`,
                    }}
                    className={classes.resizeWrapper}
                >
                    {contentChild}
                </div>
            </AccordionDetails>
        </Accordion>
    )
}
